import * as React from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
// Highcharts v12 modules self-register on import; the callable-factory
// pattern is gone from the type definitions.
import "highcharts/highcharts-more"
import "highcharts/modules/accessibility"
import "highcharts/modules/gantt"
import "highcharts/modules/treemap"
import { cn } from "@/lib/utils"

// Set default Highcharts options for consistent theming
Highcharts.setOptions({
  credits: {
    enabled: false,
  },
  chart: {
    style: {
      fontFamily: "inherit",
    },
    backgroundColor: "transparent",
  },
  title: {
    style: {
      fontSize: "14px",
      fontWeight: "600",
    },
  },
  tooltip: {
    backgroundColor: "hsl(var(--background))",
    borderColor: "hsl(var(--border))",
    borderRadius: 8,
    shadow: true,
    style: {
      color: "hsl(var(--foreground))",
      fontSize: "12px",
    },
  },
  legend: {
    itemStyle: {
      color: "hsl(var(--muted-foreground))",
      fontSize: "12px",
      fontWeight: "normal",
    },
    itemHoverStyle: {
      color: "hsl(var(--foreground))",
    },
  },
  xAxis: {
    labels: {
      style: {
        color: "hsl(var(--muted-foreground))",
        fontSize: "11px",
      },
    },
    lineColor: "hsl(var(--border))",
    tickColor: "hsl(var(--border))",
    gridLineColor: "hsl(var(--border) / 0.5)",
  },
  yAxis: {
    labels: {
      style: {
        color: "hsl(var(--muted-foreground))",
        fontSize: "11px",
      },
    },
    lineColor: "hsl(var(--border))",
    tickColor: "hsl(var(--border))",
    gridLineColor: "hsl(var(--border) / 0.5)",
  },
  plotOptions: {
    series: {
      animation: {
        duration: 500,
      },
    },
  },
})

export interface ChartProps {
  options: Highcharts.Options
  className?: string
  containerProps?: React.HTMLAttributes<HTMLDivElement>
}

// Module-constant identity is load-bearing: HighchartsReact's React.memo AND
// its update effect both key on its `containerProps` prop, so an inline object
// here gave every render a fresh identity and forced `chart.update()` (a
// visible redraw + re-animation) on EVERY parent re-render — on query-heavy
// pages, each response landing repainted every mounted chart. With a stable
// object, a chart updates only when its `options` identity actually changes.
const HIGHCHARTS_CONTAINER_PROPS = { style: { width: "100%", height: "100%" } }

/**
 * THE SERIES CONTRACT (docs/COMPONENT_PATTERNS.md "Charts") — what makes
 * live add/remove of series safe without remounting:
 *
 *  1. Every series MUST carry a stable `id`. HighchartsReact applies new
 *     options via `chart.update(options, redraw, oneToOne)`, which matches
 *     series by `id` — an id-less series falls back to POSITIONAL matching,
 *     grabbing whatever an earlier add/remove shifted into its slot and
 *     silently corrupting the chart (a freshly added series can be
 *     overwritten by the id-less one's options and the original swept away
 *     as "untouched"). Dev builds warn below when an id is missing.
 *  2. Series ORDER is the options-array order, enforced HERE: the wrapper
 *     stamps `index: i` on every series. Highcharts inserts added series by
 *     `options.index` (insertItem), and `Series.update` re-inits/re-inserts
 *     each surviving series by its fresh index, so every update pass
 *     converges to the declared order. Do NOT set `index` in options
 *     builders — array position is the single source of truth.
 *
 * Never work around chart-update bugs with a remount `key` on Chart — fix
 * the series ids instead (a remount replays the intro animation and rebuilds
 * chart state).
 */
const applySeriesContract = (options: Highcharts.Options): Highcharts.Options =>
  options.series
    ? { ...options, series: options.series.map((s, i) => ({ ...s, index: i })) }
    : options

const Chart = React.forwardRef<HighchartsReact.RefObject, ChartProps>(
  ({ options, className, containerProps }, ref) => {
    const effectiveOptions = React.useMemo(() => applySeriesContract(options), [options])

    // Dev-only contract check, deduped per chart instance so a re-render
    // storm doesn't flood the console.
    const warnedRef = React.useRef<string | null>(null)
    if (import.meta.env.DEV) {
      const missing = (options.series ?? [])
        .filter((s) => s.id == null)
        .map((s) => ("name" in s && s.name) || "(unnamed)")
      const signature = missing.join("|")
      if (missing.length > 0 && warnedRef.current !== signature) {
        warnedRef.current = signature
        console.warn(
          `[Chart] series without a stable id: ${signature}. ` +
            "Live series add/remove will corrupt this chart — give every series an `id` " +
            '(see docs/COMPONENT_PATTERNS.md "Charts").',
        )
      }
    }

    return (
      <div className={cn("w-full h-full", className)} {...containerProps}>
        <HighchartsReact
          highcharts={Highcharts}
          options={effectiveOptions}
          ref={ref}
          containerProps={HIGHCHARTS_CONTAINER_PROPS}
        />
      </div>
    )
  }
)
Chart.displayName = "Chart"

export { Chart, Highcharts, HighchartsReact }
